import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Container,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  Checkbox,
  Fab,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  ArrowBackIos,
  Add,
  Remove,
  CheckCircle,
  Search,
  ArrowForward,
  ArrowBack,
  Mail,
  MoreVert,
  PersonOutline,
  Delete,
  Close,
  Warning,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { PROGRAM_COLORS } from "../../../constants/colors";
import { getPaceDisplay } from "../../../constants/pace";
import EmailAvatar from "../../../components/EmailAvatar";
import { StudentListItem } from "../../../types/StudentModel";
import { useClassroom } from "../../../context/ClassroomContext";
import { getUnenrolledStudents } from "../../../services/studentService";
import { useNavigate, useParams } from "react-router-dom";
import {
  addStudentsToClassroom,
  emailClassroomStudents,
  getClassroom,
} from "../../../services/classroomService";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import ConfirmDeleteDialog from "../../../components/shared/ConfirmDeleteDialog";
import SendEmailDialog from "./components/SendEmailDialog";
import { useLoading } from "../../../context/LoadingContext";
import StudentProfileDialog from "./components/StudentProfileDialog";
import { useHandleError } from "../../../hooks/useHandleError";
import ChangesSummaryDialog from "./components/ChangesSummaryDialog";
import UnsavedChangesDialog from "../../../components/UnsavedChangesDialog";
import { enqueueSnackbar } from "notistack";

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
}));

// Styled components remain the same...
const StyledListItem = styled(ListItem)<{ selected: boolean }>(
  ({ theme, selected }) => ({
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    ...(selected && {
      backgroundColor: theme.palette.action.selected,
    }),
    borderBottom: `1px solid ${theme.palette.divider}`,
  })
);

const ProgramBadge = styled(Box)<{ programcolor: string }>(
  ({ theme, programcolor }) => ({
    backgroundColor: programcolor,
    color: programcolor === PROGRAM_COLORS.EAP_YELLOW ? "#000" : "#fff",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontWeight: "bold",
    marginRight: theme.spacing(3),
    minWidth: 120,
    textAlign: "center",
  })
);

const TransferButton = styled(IconButton)<{ disabled: boolean }>(
  ({ theme, disabled }) => ({
    backgroundColor: disabled
      ? theme.palette.action.disabledBackground
      : theme.palette.primary.main,
    color: disabled
      ? theme.palette.action.disabled
      : theme.palette.primary.contrastText,
    margin: theme.spacing(1, 0),
    "&:hover": {
      backgroundColor: disabled
        ? theme.palette.action.disabledBackground
        : theme.palette.primary.dark,
    },
  })
);

const FloatingActionButton = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
}));

const ClassroomStudentManagement: React.FC = () => {
  const handleError = useHandleError();

  const navigate = useNavigate();
  const { classroom, setClassroom } = useClassroom();
  const { classroomId } = useParams<{ classroomId: string }>();

  const { setIsLoading, setLoadingMessage } = useLoading();

  // Loading state for initial classroom fetch
  const [isLoadingClassroom, setIsLoadingClassroom] = useState(false);
  const [classroomFetchError, setClassroomFetchError] = useState<string | null>(
    null
  );

  // Student management state
  const [unassignedStudents, setUnassignedStudents] = useState<
    StudentListItem[]
  >([]);
  const [classroomStudents, setClassroomStudents] = useState<StudentListItem[]>(
    []
  );
  const [originalClassroomStudents, setOriginalClassroomStudents] = useState<
    StudentListItem[]
  >([]);
  const [selectedUnassigned, setSelectedUnassigned] = useState<string[]>([]);
  const [selectedClassroom, setSelectedClassroom] = useState<string[]>([]);
  const [unassignedSearch, setUnassignedSearch] = useState("");
  const [classroomSearch, setClassroomSearch] = useState("");
  const [showAllPrograms, setShowAllPrograms] = useState(false);

  // UI state
  const [studentToView, setStudentToView] = useState<StudentListItem | null>(
    null
  );
  const [showStudentProfile, setShowStudentProfile] = useState(false);
  const [studentsToRemove, setStudentsToRemove] = useState<StudentListItem[]>(
    []
  );
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenuStudent, setSelectedMenuStudent] =
    useState<StudentListItem | null>(null);

  // Email selection state
  const [emailSelectionMode, setEmailSelectionMode] = useState(false);
  const [selectedEmailStudents, setSelectedEmailStudents] = useState<string[]>(
    []
  );
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  const [studentsToEmail, setStudentsToEmail] = useState<StudentListItem[]>([]);

  // Unsaved changes state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);

  const [showChangesSummaryDialog, setShowChangesSummaryDialog] =
    useState(false);
  const [addedStudents, setAddedStudents] = useState<StudentListItem[]>([]);
  const [removedStudents, setRemovedStudents] = useState<StudentListItem[]>([]);
  const [showCrossProgramDialog, setShowCrossProgramDialog] = useState(false);
  const [crossProgramStudent, setCrossProgramStudent] =
    useState<StudentListItem | null>(null);
  const [courseStarted, setCourseStarted] = useState(false);

  // ============================================================================
  // FIX: Fetch classroom from API when navigating directly via URL
  // ============================================================================
  useEffect(() => {
    // Skip if no classroomId in URL
    if (!classroomId) {
      setClassroomFetchError("No classroom ID provided");
      return;
    }

    // Skip if we already have the correct classroom in context
    if (classroom && classroom._id === classroomId) {
      return;
    }

    const fetchClassroomData = async () => {
      setIsLoadingClassroom(true);
      setClassroomFetchError(null);

      try {
        const response = await getClassroom(classroomId);
        // The API returns { data: classroom, message: string }
        if (response.data) {
          setClassroom(response.data);
        } else {
          setClassroomFetchError("Classroom not found");
        }
      } catch (error: any) {
        console.error("Error fetching classroom:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load classroom";
        setClassroomFetchError(errorMessage);
        handleError(error);
      } finally {
        setIsLoadingClassroom(false);
      }
    };

    fetchClassroomData();
  }, [classroomId, classroom, setClassroom, handleError]);

  useEffect(() => {
    if (classroom) {
      const students = classroom.students || [];
      setClassroomStudents(students);
      setOriginalClassroomStudents([...students]); // Create a copy to avoid reference issues
    }
  }, [classroom]);

  useEffect(() => {
    if (!classroom) return;

    const fetchUnassignedStudents = async () => {
      try {
        const programFilter = showAllPrograms
          ? "all"
          : classroom?.program || "";
        const response = await getUnenrolledStudents(programFilter);
        setUnassignedStudents(response.data);
      } catch (error) {
        console.error("Error fetching unassigned students:", error);
      }
    };

    fetchUnassignedStudents();
  }, [classroom?.program, showAllPrograms]);

  // Check for unsaved changes whenever relevant state changes
  useEffect(() => {
    if (!originalClassroomStudents.length) return; // Skip initial render

    // Check if classroom students have changed
    const haveStudentsChanged = () => {
      // Check lengths first
      if (classroomStudents.length !== originalClassroomStudents.length) {
        return true;
      }

      // Create maps for efficient look-up
      const originalMap = new Map(
        originalClassroomStudents.map((student) => [student.id, student])
      );
      const currentMap = new Map(
        classroomStudents.map((student) => [student.id, student])
      );

      // Check if any students were added or removed
      for (const student of originalClassroomStudents) {
        if (!currentMap.has(student.id)) {
          return true; // A student was removed
        }
      }

      for (const student of classroomStudents) {
        if (!originalMap.has(student.id)) {
          return true; // A student was added
        }
      }

      return false;
    };

    setHasUnsavedChanges(haveStudentsChanged());
  }, [classroomStudents, originalClassroomStudents]);

  // Reset selection mode when navigating away
  useEffect(() => {
    return () => {
      setEmailSelectionMode(false);
      setSelectedEmailStudents([]);
    };
  }, []);

  // Handle window/tab close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ""; // This is required for Chrome
        return ""; // This text is usually ignored by browsers
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // ============================================================================
  // Loading State: Show spinner while fetching classroom
  // ============================================================================
  if (isLoadingClassroom) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, mb: 8 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 400,
            gap: 2,
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading classroom...
          </Typography>
        </Box>
      </Container>
    );
  }

  // ============================================================================
  // Error State: Show error if classroom fetch failed
  // ============================================================================
  if (classroomFetchError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, mb: 8 }}>
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/teacher/classrooms")}
            >
              Go Back
            </Button>
          }
        >
          {classroomFetchError}
        </Alert>
      </Container>
    );
  }

  // ============================================================================
  // No Classroom State: Handle case where classroom is still null
  // ============================================================================
  if (!classroom) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, mb: 8 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 400,
            gap: 2,
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading classroom...
          </Typography>
        </Box>
      </Container>
    );
  }

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    student: StudentListItem
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedMenuStudent(student);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMenuStudent(null);
  };

  const filteredUnassigned = unassignedStudents.filter((student) =>
    student.name.toLowerCase().includes(unassignedSearch.toLowerCase())
  );

  const filteredClassroom = classroomStudents.filter((student) =>
    student.name.toLowerCase().includes(classroomSearch.toLowerCase())
  );

  const toggleUnassignedSelection = (student: StudentListItem) => {
    if (emailSelectionMode) {
      // In email selection mode, don't allow selecting unassigned students
      return;
    }

    setSelectedUnassigned((prev) =>
      prev.includes(student.id)
        ? prev.filter((id) => id !== student.id)
        : [...prev, student.id]
    );
  };

  const toggleClassroomSelection = (student: StudentListItem) => {
    if (emailSelectionMode) {
      // In email selection mode, toggle email selection instead
      toggleEmailSelection(student);
      return;
    }

    setSelectedClassroom((prev) =>
      prev.includes(student.id)
        ? prev.filter((id) => id !== student.id)
        : [...prev, student.id]
    );
  };

  const toggleEmailSelection = (student: StudentListItem) => {
    setSelectedEmailStudents((prev) =>
      prev.includes(student.id)
        ? prev.filter((id) => id !== student.id)
        : [...prev, student.id]
    );
  };

  const addStudentToClassroom = (student: StudentListItem) => {
    if (emailSelectionMode) return; // Disable during email selection mode

    // Check if student is from a different program
    if (
      student.programName &&
      classroom?.program &&
      student.programName !== classroom.program
    ) {
      setCrossProgramStudent(student);
      setShowCrossProgramDialog(true);
      return;
    }

    setUnassignedStudents((prev) => prev.filter((s) => s.id !== student.id));
    setClassroomStudents((prev) => [...prev, student]);
  };

  const confirmCrossProgramAdd = () => {
    if (crossProgramStudent) {
      setUnassignedStudents((prev) =>
        prev.filter((s) => s.id !== crossProgramStudent.id)
      );
      setClassroomStudents((prev) => [...prev, crossProgramStudent]);
    }
    setShowCrossProgramDialog(false);
    setCrossProgramStudent(null);
  };

  const removeStudentFromClassroom = (student: StudentListItem) => {
    if (emailSelectionMode) return; // Disable during email selection mode

    // Check if student was originally in the classroom
    const wasOriginalStudent = originalClassroomStudents.some(
      (s) => s.id === student.id
    );

    if (wasOriginalStudent) {
      // Prompt for confirmation if student was originally enrolled
      setStudentsToRemove([student]);
      setShowRemoveDialog(true);
    } else {
      // Directly remove if student was just added during this session
      setClassroomStudents((prev) => prev.filter((s) => s.id !== student.id));
      setUnassignedStudents((prev) => [...prev, student]);
    }
  };

  const confirmRemoveStudents = () => {
    for (const student of studentsToRemove) {
      setClassroomStudents((prev) => prev.filter((s) => s.id !== student.id));
      setUnassignedStudents((prev) => [...prev, student]);
    }
    setShowRemoveDialog(false);
    setStudentsToRemove([]);
  };

  const addSelectedStudents = () => {
    if (emailSelectionMode) return; // Disable during email selection mode

    const studentsToAdd = unassignedStudents.filter((s) =>
      selectedUnassigned.includes(s.id)
    );
    setClassroomStudents((prev) => [...prev, ...studentsToAdd]);
    setUnassignedStudents((prev) =>
      prev.filter((s) => !selectedUnassigned.includes(s.id))
    );
    setSelectedUnassigned([]);
  };

  const removeSelectedStudents = () => {
    if (emailSelectionMode) return; // Disable during email selection mode

    const toRemove = classroomStudents.filter((s) =>
      selectedClassroom.includes(s.id)
    );

    // Check if any selected students were originally in the classroom
    const originalStudentsSelected = toRemove.some((student) =>
      originalClassroomStudents.some((original) => original.id === student.id)
    );

    if (originalStudentsSelected) {
      // Prompt for confirmation
      setStudentsToRemove(toRemove);
      setShowRemoveDialog(true);
    } else {
      // Directly remove if none were originally enrolled
      setUnassignedStudents((prev) => [...prev, ...toRemove]);
      setClassroomStudents((prev) =>
        prev.filter((s) => !selectedClassroom.includes(s.id))
      );
    }

    setSelectedClassroom([]);
  };

  const viewStudentProfile = (student: StudentListItem) => {
    setStudentToView(student);
    setShowStudentProfile(true);
    handleMenuClose();
  };

  const toggleEmailSelectionMode = () => {
    // Toggle email selection mode
    setEmailSelectionMode(!emailSelectionMode);
    // Clear any existing selections when toggling
    setSelectedEmailStudents([]);
  };

  const handleSendSelectedEmails = () => {
    if (selectedEmailStudents.length === 0) {
      return;
    }

    const selectedStudents = classroomStudents.filter((student) =>
      selectedEmailStudents.includes(student.id)
    );

    setStudentsToEmail(selectedStudents);
    setShowEmailDialog(true);
  };

  const handleSendBulkEmails = () => {
    setStudentsToEmail(classroomStudents);
    setShowBulkEmailDialog(true);
  };

  const handleSendEmail = async (subject: string, message: string) => {
    if (!classroomId || studentsToEmail.length === 0) return;

    try {
      const response = await emailClassroomStudents(classroomId, {
        subject,
        message,
        studentIds: studentsToEmail.map((student) => student.id),
      });

      const { sent, failed } = response?.data || {};
      const recipientText =
        sent === 1 && studentsToEmail.length === 1
          ? studentsToEmail[0].name
          : `${sent} students`;

      setSuccessMessage(
        failed
          ? `Email sent to ${recipientText} (${failed} failed)`
          : `Email sent successfully to ${recipientText}`
      );

      // Exit email selection mode if active
      if (emailSelectionMode) {
        setEmailSelectionMode(false);
        setSelectedEmailStudents([]);
      }
    } catch (error: any) {
      console.error("Error sending emails:", error);
      enqueueSnackbar(
        error?.response?.data?.message || "Could not send emails.",
        { variant: "error" }
      );
    }
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesDialog(true);
    } else {
      navigate("/teacher/classrooms");
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesDialog(false);
    navigate("/teacher/classrooms");
  };

  const handleSave = async () => {
    if (!classroom._id) {
      return;
    }

    // Find newly added students
    const newlyAdded = classroomStudents.filter(
      (student) =>
        !originalClassroomStudents.some(
          (original) => original.id === student.id
        )
    );

    // Find removed students
    const removed = originalClassroomStudents.filter(
      (student) =>
        !classroomStudents.some((current) => current.id === student.id)
    );

    // Determine if course has started already
    const courseStarted = new Date(classroom.startDate) <= new Date();

    // If there are changes, show the confirmation dialog
    if (newlyAdded.length > 0 || removed.length > 0) {
      setAddedStudents(newlyAdded);
      setRemovedStudents(removed);
      setCourseStarted(courseStarted);
      setShowChangesSummaryDialog(true);
    } else {
      // No changes to confirm, just close
      setHasUnsavedChanges(false);
    }
  };

  const saveChangesConfirmed = async (emailOptions: {
    sendToAdded: boolean;
    sendToRemoved: boolean;
  }): Promise<void> => {
    try {
      if (!classroom || !classroom._id) {
        throw new Error("Classroom ID is missing");
      }

      setIsLoading(true);
      setLoadingMessage("Saving classroom changes...");

      // Save the student changes to the classroom
      const response = await addStudentsToClassroom(
        classroom._id,
        classroomStudents,
        emailOptions // Pass emailOptions to the backend
      );

      if (response.message) {
        enqueueSnackbar(response.message, {
          variant: "success",
        });
      }

      // Send emails based on options
      // if (emailOptions.sendToAdded && addedStudents.length > 0) {
      //   setLoadingMessage("Sending welcome emails...");
      //   // Call your email API for welcome emails
      //   setStudentsToEmail(addedStudents);
      //   // Here you could either open the email dialog or directly send with a template
      //   setShowEmailDialog(true);
      // }

      // if (emailOptions.sendToRemoved && removedStudents.length > 0) {
      //   setLoadingMessage("Sending notification emails...");
      //   // Call your email API for removal/unenrollment emails
      //   // Implement the email sending logic for removed students
      // }

      // Mark changes as saved
      setHasUnsavedChanges(false);
      setShowChangesSummaryDialog(false);

      return;
    } catch (error) {
      handleError(error);
      console.error("Error saving students:", error);
      return;
    } finally {
      setIsLoading(false);
    }
  };
  const getProgramColor = (program: string) => {
    if (program.includes("YELLOW")) return PROGRAM_COLORS.EAP_YELLOW;
    if (program.includes("GREEN")) return PROGRAM_COLORS.EAP_GREEN;
    return PROGRAM_COLORS.EAP_ACCESS;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 8 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleNavigateBack();
          }}
          underline="hover"
        >
          Classrooms
        </Link>
        <Typography color="text.primary">
          Manage Students - {classroom?.program}
        </Typography>
      </Breadcrumbs>

      {hasUnsavedChanges && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have unsaved changes. Don't forget to save before leaving this
          page.
        </Alert>
      )}

      {emailSelectionMode && (
        <Box
          sx={{
            bgcolor: "primary.light",
            color: "primary.contrastText",
            p: 2,
            borderRadius: 1,
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Email Selection Mode
            </Typography>
            <Typography variant="body2">
              {selectedEmailStudents.length} student
              {selectedEmailStudents.length !== 1 ? "s" : ""} selected
            </Typography>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              disabled={selectedEmailStudents.length === 0}
              onClick={handleSendSelectedEmails}
              startIcon={<Mail />}
              sx={{ mr: 1 }}
            >
              Send Email
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={toggleEmailSelectionMode}
              startIcon={<Close />}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <ProgramBadge programcolor={getProgramColor(classroom?.program)}>
          {classroom?.program}
        </ProgramBadge>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          <Box>
            <DetailLabel>START DATE</DetailLabel>
            <Typography variant="body2">
              {formatDateForDisplay(classroom?.startDate)}
            </Typography>
          </Box>

          <Box>
            <DetailLabel>END DATE</DetailLabel>
            <Typography variant="body2">
              {formatDateForDisplay(classroom?.endDate)}
            </Typography>
          </Box>

          <Box>
            <DetailLabel>PACE</DetailLabel>
            <Typography variant="body2">
              {getPaceDisplay(classroom?.pace)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Rest of the component remains the same... */}
        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ height: "100%", p: 2, borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  Unassigned Students
                </Typography>
                <Chip
                  label={filteredUnassigned.length}
                  size="small"
                  sx={{ ml: 1, bgcolor: "grey.200" }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showAllPrograms}
                    onChange={(e) => setShowAllPrograms(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption">All Programs</Typography>
                }
              />
            </Box>

            <TextField
              placeholder="Search students..."
              variant="outlined"
              size="small"
              fullWidth
              value={unassignedSearch}
              onChange={(e) => setUnassignedSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Search fontSize="small" color="action" sx={{ mr: 1 }} />
                ),
              }}
              sx={{ mb: 2 }}
            />

            <List
              sx={{
                maxHeight: 400,
                overflow: "auto",
                bgcolor: "background.paper",
                borderRadius: 1,
              }}
            >
              {filteredUnassigned.map((student) => (
                <StyledListItem
                  key={student.id}
                  selected={selectedUnassigned.includes(student.id)}
                  onClick={() => toggleUnassignedSelection(student)}
                  disabled={emailSelectionMode}
                  sx={{ opacity: emailSelectionMode ? 0.6 : 1 }}
                >
                  <ListItemAvatar>
                    <EmailAvatar email={student.name} size={40} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body1" fontWeight={500}>
                          {student.name}
                        </Typography>
                        {student.programName && (
                          <Chip
                            label={student.programName}
                            size="small"
                            sx={{
                              bgcolor: getProgramColor(student.programName),
                              color: student.programName.includes("YELLOW")
                                ? "#000"
                                : "#fff",
                              fontSize: "0.65rem",
                              height: 20,
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondaryTypographyProps={{ fontSize: "0.75rem" }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        viewStudentProfile(student);
                      }}
                      size="small"
                      disabled={emailSelectionMode}
                    >
                      <PersonOutline fontSize="small" />
                    </IconButton>
                    <Button
                      startIcon={<Add />}
                      onClick={(e) => {
                        e.stopPropagation();
                        addStudentToClassroom(student);
                      }}
                      size="small"
                      disabled={emailSelectionMode}
                    >
                      Add
                    </Button>
                  </ListItemSecondaryAction>
                </StyledListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          md={1}
          sx={{
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <TransferButton
            onClick={addSelectedStudents}
            disabled={selectedUnassigned.length === 0 || emailSelectionMode}
            title="Add selected students"
            size="small"
          >
            <ArrowForward />
          </TransferButton>
          <TransferButton
            onClick={removeSelectedStudents}
            disabled={selectedClassroom.length === 0 || emailSelectionMode}
            title="Remove selected students"
            size="small"
          >
            <ArrowBack />
          </TransferButton>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ height: "100%", p: 2, borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  Classroom Students
                </Typography>
                <Chip
                  label={filteredClassroom.length}
                  size="small"
                  sx={{ ml: 1, bgcolor: "grey.200" }}
                />
              </Box>
              {classroomStudents.length > 0 && !emailSelectionMode && (
                <Box>
                  <Tooltip title="Select students to email">
                    <Button
                      startIcon={<Mail />}
                      size="small"
                      onClick={toggleEmailSelectionMode}
                      sx={{ mr: 1 }}
                    >
                      Select for Email
                    </Button>
                  </Tooltip>
                  <Tooltip title="Send enrollment emails to all students">
                    <Button
                      startIcon={<Mail />}
                      size="small"
                      onClick={handleSendBulkEmails}
                      color="primary"
                    >
                      Email All
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </Box>

            <TextField
              placeholder="Search students..."
              variant="outlined"
              size="small"
              fullWidth
              value={classroomSearch}
              onChange={(e) => setClassroomSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Search fontSize="small" color="action" sx={{ mr: 1 }} />
                ),
              }}
              sx={{ mb: 2 }}
            />

            <List
              sx={{
                maxHeight: 400,
                overflow: "auto",
                bgcolor: "background.paper",
                borderRadius: 1,
              }}
            >
              {filteredClassroom.length > 0 ? (
                filteredClassroom.map((student) => (
                  <StyledListItem
                    key={student.id}
                    selected={
                      emailSelectionMode
                        ? selectedEmailStudents.includes(student.id)
                        : selectedClassroom.includes(student.id)
                    }
                    onClick={() => toggleClassroomSelection(student)}
                  >
                    {emailSelectionMode && (
                      <Checkbox
                        edge="start"
                        checked={selectedEmailStudents.includes(student.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleEmailSelection(student)}
                      />
                    )}
                    <ListItemAvatar>
                      <EmailAvatar email={student.name} size={40} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={student.name}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={{ fontSize: "0.75rem" }}
                    />
                    <ListItemSecondaryAction>
                      {!emailSelectionMode && (
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, student);
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </StyledListItem>
                ))
              ) : (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", color: "text.secondary" }}
                  >
                    No students added to this classroom
                  </Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" color="inherit" onClick={handleNavigateBack}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CheckCircle />}
          onClick={handleSave}
          disabled={emailSelectionMode}
        >
          Save Changes
        </Button>
      </Box>

      {/* Floating action button for email selection in mobile view */}
      {emailSelectionMode && selectedEmailStudents.length > 0 && (
        <FloatingActionButton
          color="primary"
          onClick={handleSendSelectedEmails}
        >
          <Mail />
        </FloatingActionButton>
      )}

      {/* Student action menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() =>
            selectedMenuStudent && viewStudentProfile(selectedMenuStudent)
          }
        >
          <ListItemAvatar>
            <PersonOutline fontSize="small" />
          </ListItemAvatar>
          <ListItemText>View Profile</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedMenuStudent) {
              setStudentsToEmail([selectedMenuStudent]);
              setShowEmailDialog(true);
              handleMenuClose();
            }
          }}
        >
          <ListItemAvatar>
            <Mail fontSize="small" />
          </ListItemAvatar>
          <ListItemText>Send Enrollment Email</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() =>
            selectedMenuStudent &&
            removeStudentFromClassroom(selectedMenuStudent)
          }
        >
          <ListItemAvatar>
            <Delete fontSize="small" color="error" />
          </ListItemAvatar>
          <ListItemText sx={{ color: "error.main" }}>Remove</ListItemText>
        </MenuItem>
      </Menu>

      {/* Student profile dialog */}
      <StudentProfileDialog
        open={showStudentProfile}
        onClose={() => setShowStudentProfile(false)}
        student={studentToView}
      />

      {/* Send email dialog */}
      <SendEmailDialog
        open={showEmailDialog}
        onClose={() => {
          setShowEmailDialog(false);
          setStudentsToEmail([]);
        }}
        students={studentsToEmail}
        onSend={handleSendEmail}
      />

      {/* Bulk email dialog */}
      <SendEmailDialog
        open={showBulkEmailDialog}
        onClose={() => {
          setShowBulkEmailDialog(false);
          setStudentsToEmail([]);
        }}
        students={studentsToEmail}
        onSend={handleSendEmail}
      />

      {/* Confirm remove dialog */}
      <ConfirmDeleteDialog
        open={showRemoveDialog}
        onClose={() => setShowRemoveDialog(false)}
        onConfirm={confirmRemoveStudents}
        entityName={
          studentsToRemove.length === 1
            ? studentsToRemove[0].name
            : `${studentsToRemove.length} students`
        }
        confirmationText="REMOVE"
        title="Remove Student(s)"
        description="Are you sure you want to remove the following student(s) from this classroom?"
      />

      {/* Changes Summary Dialog */}

      <ChangesSummaryDialog
        open={showChangesSummaryDialog}
        onClose={() => setShowChangesSummaryDialog(false)}
        addedStudents={addedStudents}
        removedStudents={removedStudents}
        courseStarted={courseStarted}
        onConfirm={saveChangesConfirmed}
        onCancel={() => setShowChangesSummaryDialog(false)}
      />

      {/* Cross-program confirmation dialog */}
      <Dialog
        open={showCrossProgramDialog}
        onClose={() => {
          setShowCrossProgramDialog(false);
          setCrossProgramStudent(null);
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Warning color="warning" />
            Different Program
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{crossProgramStudent?.name}</strong> was assigned to{" "}
            <strong>{crossProgramStudent?.programName}</strong> but will receive{" "}
            <strong>{classroom?.program}</strong> course content in this
            classroom. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowCrossProgramDialog(false);
              setCrossProgramStudent(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmCrossProgramAdd}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved changes dialog */}
      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onClose={() => setShowUnsavedChangesDialog(false)}
        onDiscard={handleDiscardChanges}
      />

      {/* Success dialog with option to send emails */}
      {/* <Dialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          setHasUnsavedChanges(false); // Mark as saved after success
          navigate("/teacher/classrooms");
        }}
      >
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <DialogContentText>{successMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSuccessDialog(false);
              setHasUnsavedChanges(false); // Mark as saved
              navigate("/teacher/classrooms");
            }}
            color="inherit"
          >
            No, Go Back
          </Button>
          {studentsToEmail.length > 0 && (
            <Button
              onClick={() => {
                setShowSuccessDialog(false);
                setShowBulkEmailDialog(true);
              }}
              color="primary"
              variant="contained"
              startIcon={<Mail />}
            >
              Yes, Send Emails
            </Button>
          )}
        </DialogActions>
      </Dialog> */}
    </Container>
  );
};

export default ClassroomStudentManagement;
